import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Mail, Instagram, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
// =====================================================================
// TODO: Replace YOUR_FORM_ID with your actual Formspree form ID
// 1. Sign up at https://formspree.io
// 2. Create a new form, verify your email
// 3. Copy the form ID (looks like 'xyzabcde' or 'mldwbpra')
// 4. Paste it below
// =====================================================================
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    // Honeypot spam check — if the hidden field is filled, it's a bot
    if (formData.get('_gotcha')) {
      setStatus('success'); // pretend success to fool bots
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1613">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-20 px-6 border-b border-darkgray" data-id="element-1614">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1615">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-1616">
            <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1617">
              Get In Touch
            </motion.p>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-tight mb-8" data-id="element-1618">
              Let's build your structure.
            </motion.h1>
            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-8" data-id="element-1619" />
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] max-w-2xl leading-relaxed" data-id="element-1620">
              Whether you're starting GLP-1 therapy, navigating side effects, or
              planning a transition off medication — I'd love to hear from you.
              Send a note and I'll respond within 48 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-1621">
        <div className="max-w-7xl mx-auto" data-id="element-1622">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16" data-id="element-1623">
            {/* FORM */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="lg:col-span-2" data-id="element-1624">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-3" data-id="element-1625">
                Send a Message
              </p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-10" data-id="element-1626">
                Tell me a bit about yourself.
              </h2>

              {status === 'success' ? <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-1627">
                  <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-1628" />
                  <div data-id="element-1629">
                    <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-1630">
                      Message sent.
                    </h3>
                    <p className="text-[#3B3B3B] leading-relaxed mb-4" data-id="element-1631">
                      Thanks for reaching out — I'll get back to you within 48
                      hours. Keep an eye on your inbox (including the spam
                      folder, just in case).
                    </p>
                    <button onClick={() => setStatus('idle')} className="text-xs font-bold text-tealslate uppercase tracking-wider hover:text-navyslate transition-colors inline-flex items-center gap-2" data-id="element-1632">
                      Send another message <ArrowRight className="w-3 h-3" data-id="element-1633" />
                    </button>
                  </div>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1634">
                  {/* Honeypot — hidden, bots will fill it */}
                  <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px'
              }} aria-hidden="true" data-id="element-1635" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1636">
                    <div data-id="element-1637">
                      <label htmlFor="name" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1638">
                        Full Name *
                      </label>
                      <input id="name" name="name" type="text" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1639" />
                    </div>
                    <div data-id="element-1640">
                      <label htmlFor="email" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1641">
                        Email *
                      </label>
                      <input id="email" name="email" type="email" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1642" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1643">
                    <div data-id="element-1644">
                      <label htmlFor="phone" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1645">
                        Phone{' '}
                        <span className="text-[#9A9A90] font-normal normal-case tracking-normal" data-id="element-1646">
                          (optional)
                        </span>
                      </label>
                      <input id="phone" name="phone" type="tel" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1647" />
                    </div>
                    <div data-id="element-1648">
                      <label htmlFor="interest" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1649">
                        I'm Interested In *
                      </label>
                      <select id="interest" name="interest" required defaultValue="" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1650">
                        <option value="" disabled data-id="element-1651">
                          Select one…
                        </option>
                        <option value="GLP-1 Nutrition Coaching" data-id="element-1652">
                          1:1 GLP-1 Nutrition Coaching
                        </option>
                        <option value="Body Composition & Muscle" data-id="element-1653">
                          Body Composition & Muscle
                        </option>
                        <option value="Transitioning Off Medication" data-id="element-1654">
                          Transitioning Off Medication
                        </option>
                        <option value="Supplement Recommendations" data-id="element-1655">
                          Supplement Recommendations
                        </option>
                        <option value="General Inquiry" data-id="element-1656">General Inquiry</option>
                        <option value="Press / Speaking" data-id="element-1657">
                          Press / Speaking
                        </option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-1658">
                    <label htmlFor="message" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1659">
                      Message *
                    </label>
                    <textarea id="message" name="message" required rows={6} placeholder="Tell me about your goals and where you are on your journey…" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-1660" />
                  </div>

                  {status === 'error' && <div className="bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1661">
                      <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1662" />
                      <p className="text-sm text-burgundy" data-id="element-1663">{errorMessage}</p>
                    </div>}

                  <div className="pt-2" data-id="element-1664">
                    <button type="submit" disabled={status === 'submitting'} className="px-10 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-3" data-id="element-1665">
                      {status === 'submitting' ? <>
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1666" />
                          Sending…
                        </> : <>
                          Send Message <ArrowRight className="w-4 h-4" data-id="element-1667" />
                        </>}
                    </button>
                  </div>

                  <p className="text-xs text-[#7A7A70] italic pt-4" data-id="element-1668">
                    Your information stays private. I respond personally to
                    every message.
                  </p>
                </form>}
            </motion.div>

            {/* INFO SIDEBAR */}
            <motion.aside initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="space-y-6" data-id="element-1669">
              <div className="bg-charcoal text-white p-8 border-l-4 border-tealslate" data-id="element-1670">
                <Clock className="w-6 h-6 text-tealslate mb-4" data-id="element-1671" />
                <h3 className="text-xl font-serif text-white mb-3" data-id="element-1672">
                  Response Time
                </h3>
                <p className="text-sm text-[#D8D8D0] leading-relaxed mb-4" data-id="element-1673">
                  I personally read and reply to every message within 48 hours,
                  Monday through Friday.
                </p>
                <p className="text-xs text-[#9CA3AF] italic" data-id="element-1674">
                  For urgent medical questions, please contact your prescribing
                  physician.
                </p>
              </div>

              <div className="bg-white border border-[#D8D8D0] p-8" data-id="element-1675">
                <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1676">
                  Other Ways To Connect
                </p>
                <ul className="space-y-4" data-id="element-1677">
                  <li data-id="element-1678">
                    <a href="#" className="flex items-center gap-3 text-sm text-[#1F1F1F] hover:text-tealslate transition-colors group" data-id="element-1679">
                      <Instagram className="w-5 h-5 text-tealslate" data-id="element-1680" />
                      <span data-id="element-1681">
                        <span className="block font-bold" data-id="element-1682">Instagram</span>
                        <span className="block text-xs text-[#7A7A70]" data-id="element-1683">
                          @jackachenbach
                        </span>
                      </span>
                    </a>
                  </li>
                  <li className="pt-4 border-t border-[#D8D8D0]" data-id="element-1684">
                    <div className="flex items-center gap-3 text-sm text-[#1F1F1F]" data-id="element-1685">
                      <Mail className="w-5 h-5 text-tealslate" data-id="element-1686" />
                      <span data-id="element-1687">
                        <span className="block font-bold" data-id="element-1688">Prefer Email?</span>
                        <span className="block text-xs text-[#7A7A70]" data-id="element-1689">
                          jack [at] jackachenbach [dot] com
                        </span>
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-burgundy text-white p-8" data-id="element-1690">
                <h3 className="text-xl font-serif text-white mb-3" data-id="element-1691">
                  Ready to start?
                </h3>
                <p className="text-sm text-[#D8D8D0] leading-relaxed mb-6" data-id="element-1692">
                  Skip the back-and-forth and book a free 15-minute discovery
                  call directly.
                </p>
                <a href="#" className="inline-flex items-center gap-2 text-xs font-bold text-white uppercase tracking-wider border-b border-tealslate pb-1 hover:text-tealslate transition-colors" data-id="element-1693">
                  Book Discovery Call <ArrowRight className="w-3 h-3" data-id="element-1694" />
                </a>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>
    </div>;
}